import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Calendar, Truck, User, CheckCircle, ChevronRight, Minus, Plus, Trash2, Package, MapPin, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import AvailabilityCalendar from "./AvailabilityCalendar";
import { api, BASE } from "../../services/api";

const SERVICE_OPTIONS = [
    {
        id: "pickup",
        icon: Package,
        label: "Vyzdvihnutie na sklade",
        desc: "Prídete si vyzdvihnúť techniku nachystanú u nás. Bez príplatku.",
        price: null,
    },
    {
        id: "delivery",
        icon: Truck,
        label: "Dovoz a odvoz",
        desc: "Dovezieme techniku na miesto konania a po skončení odvezieme.",
        price: "Cena dovozu dohodou",
    },
    {
        id: "delivery_setup",
        icon: Wrench,
        label: "Dovoz + inštalácia + obsluha",
        desc: "Kompletný servis — dovoz, profesionálna inštalácia a obsluha počas celého eventu.",
        price: "Cena dohodou",
    },
];

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function formatDate(d) {
    if (!d) return "—";
    return d.toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" });
}

function daysBetween(start, end) {
    if (!start || !end) return 0;
    return Math.round((end - start) / 86400000) + 1;
}

// ── Step indicators ───────────────────────────────────────────────────────────
function Steps({ current }) {
    const steps = [
        { id: 1, label: "Košík"   },
        { id: 2, label: "Termín"  },
        { id: 3, label: "Kontakt" },
        { id: 4, label: "Hotovo"  },
    ];
    return (
        <div className="flex items-center gap-0 px-6 py-3 border-b border-white/8 bg-nova-black/20">
            {steps.map((s, i) => (
                <React.Fragment key={s.id}>
                    <div className="flex items-center gap-1.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                            s.id < current ? "bg-nova-cyan text-black" :
                            s.id === current ? "bg-nova-cyan text-black" :
                            "bg-white/10 text-zinc-600"
                        }`}>
                            {s.id < current ? "✓" : s.id}
                        </div>
                        <span className={`text-xs font-medium hidden sm:block ${s.id === current ? "text-white" : "text-zinc-600"}`}>
                            {s.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`flex-1 h-px mx-2 transition-colors ${s.id < current ? "bg-nova-cyan/40" : "bg-white/8"}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ── Step 1: Cart ──────────────────────────────────────────────────────────────
function StepCart({ items, updateQty, removeItem, onNext }) {
    const allPriced = items.every(i => i.equipment.rental_price_day > 0);
    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
                {items.map(({ equipment: eq, qty }) => (
                    <div key={eq.id} className="flex gap-3 items-center p-3 rounded-xl border border-white/8 bg-nova-black/40">
                        <div className="w-14 h-12 rounded-lg overflow-hidden bg-nova-surface/50 shrink-0">
                            {eq.image_url
                                ? <img src={resolveImg(eq.image_url)} alt="" className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">—</div>
                            }
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-semibold truncate">{eq.brand} {eq.model}</div>
                            <div className="text-zinc-500 text-xs font-mono">{eq.category}</div>
                            {eq.rental_price_day > 0 ? (
                                <div className="text-nova-cyan text-xs font-mono mt-0.5">{eq.rental_price_day}€/deň × {qty} = {(eq.rental_price_day * qty).toFixed(0)}€</div>
                            ) : (
                                <div className="text-zinc-600 text-xs mt-0.5">Cena na dopyt</div>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={() => updateQty(eq.id, qty - 1)}
                                className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                                <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-white text-sm font-mono w-5 text-center">{qty}</span>
                            <button onClick={() => updateQty(eq.id, eq.rental_qty ? Math.min(qty + 1, eq.rental_qty) : qty + 1)}
                                className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                                <Plus className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeItem(eq.id)}
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-600 hover:text-red-400 transition-colors ml-1">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="px-6 py-4 border-t border-white/8 space-y-3">
                {allPriced && items.length > 0 && (
                    <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Orientačná cena / deň</span>
                        <span className="text-white font-semibold">
                            {items.reduce((s,i) => s + i.equipment.rental_price_day * i.qty, 0).toFixed(0)}€
                        </span>
                    </div>
                )}
                <button onClick={onNext}
                    className="w-full h-11 rounded-xl bg-nova-cyan text-black font-bold text-sm hover:saturate-125 transition-all flex items-center justify-center gap-2">
                    Vybrať termín <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ── Step 2: Dates ─────────────────────────────────────────────────────────────
function StepDates({ items, startDate, endDate, onChange, onNext, onBack }) {
    const equipmentIds = items.map(i => i.equipment.id);
    const days = daysBetween(startDate, endDate);
    const canContinue = startDate && endDate;

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                <div>
                    <p className="text-zinc-500 text-xs mb-3 leading-relaxed">
                        Vyberte dátum začiatku a konca prenájmu. Šedé dni sú obsadené.
                    </p>
                    <AvailabilityCalendar
                        equipmentIds={equipmentIds}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={onChange}
                    />
                </div>
                {startDate && (
                    <div className="p-3 rounded-xl bg-nova-cyan/8 border border-nova-cyan/20">
                        <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-3.5 h-3.5 text-nova-cyan shrink-0" />
                            <span className="text-zinc-400">Od:</span>
                            <span className="text-white font-medium">{formatDate(startDate)}</span>
                        </div>
                        {endDate && (
                            <>
                                <div className="flex items-center gap-2 text-sm mt-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-nova-cyan shrink-0" />
                                    <span className="text-zinc-400">Do:</span>
                                    <span className="text-white font-medium">{formatDate(endDate)}</span>
                                </div>
                                <div className="mt-2 text-nova-cyan font-mono text-xs">
                                    {days} {days === 1 ? "deň" : days < 5 ? "dni" : "dní"} prenájmu
                                </div>
                            </>
                        )}
                        {!endDate && <p className="text-zinc-500 text-xs mt-1.5">Kliknite na dátum konca…</p>}
                    </div>
                )}
            </div>
            <div className="px-6 py-4 border-t border-white/8 flex gap-3">
                <button onClick={onBack} className="h-11 px-5 rounded-xl border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">
                    Späť
                </button>
                <button onClick={onNext} disabled={!canContinue}
                    className="flex-1 h-11 rounded-xl bg-nova-cyan text-black font-bold text-sm hover:saturate-125 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                    Pokračovať <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ── Step 3: Contact ───────────────────────────────────────────────────────────
function StepContact({ items, startDate, endDate, onBack, onDone }) {
    const [serviceType, setServiceType] = useState("pickup");
    const [form, setForm] = useState({ name: "", email: "", phone: "", delivery_address: "", message: "" });
    const [sending, setSending] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const days = daysBetween(startDate, endDate);

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim()) { toast.error("Vyplňte meno a email."); return; }
        if (serviceType !== "pickup" && !form.delivery_address.trim()) { toast.error("Zadajte adresu doručenia."); return; }
        setSending(true);
        try {
            await api.createBooking({
                items: items.map(i => ({
                    equipment_id: i.equipment.id,
                    equipment_name: `${i.equipment.brand} ${i.equipment.model}`,
                    qty: i.qty,
                    price_per_day: i.equipment.rental_price_day || 0,
                })),
                start_date: startDate.toISOString().slice(0, 10),
                end_date:   endDate.toISOString().slice(0, 10),
                service_type: serviceType,
                delivery_address: form.delivery_address,
                name:    form.name,
                email:   form.email,
                phone:   form.phone,
                message: form.message,
            });
            onDone();
        } catch (e) {
            toast.error(e.message);
            setSending(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Summary bar */}
                <div className="p-3 rounded-xl bg-nova-surface/30 border border-white/8 text-xs space-y-1">
                    <div className="flex gap-2">
                        <span className="text-zinc-500 w-16 shrink-0">Termín:</span>
                        <span className="text-white font-medium">{formatDate(startDate)} – {formatDate(endDate)} ({days} {days===1?"deň":days<5?"dni":"dní"})</span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-zinc-500 w-16 shrink-0">Technika:</span>
                        <span className="text-zinc-300">{items.map(i=>`${i.equipment.brand} ${i.equipment.model} ×${i.qty}`).join(", ")}</span>
                    </div>
                </div>

                {/* Service type */}
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Spôsob odovzdania</label>
                    <div className="space-y-2">
                        {SERVICE_OPTIONS.map(opt => {
                            const Icon = opt.icon;
                            return (
                                <button key={opt.id} type="button" onClick={() => setServiceType(opt.id)}
                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                        serviceType === opt.id
                                            ? "border-nova-cyan/50 bg-nova-cyan/8"
                                            : "border-white/8 bg-nova-black/30 hover:border-white/20"
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${serviceType===opt.id?"bg-nova-cyan/20":"bg-white/5"}`}>
                                            <Icon className={`w-4 h-4 ${serviceType===opt.id?"text-nova-cyan":"text-zinc-500"}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-semibold ${serviceType===opt.id?"text-white":"text-zinc-300"}`}>{opt.label}</div>
                                            <div className="text-zinc-500 text-xs mt-0.5">{opt.desc}</div>
                                            {opt.price && <div className="text-nova-cyan/70 text-[10px] font-mono mt-1">{opt.price}</div>}
                                        </div>
                                        <div className={`w-4 h-4 rounded-full border-2 mt-1 shrink-0 transition-colors ${serviceType===opt.id?"border-nova-cyan bg-nova-cyan":"border-zinc-600"}`} />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Address (if delivery) */}
                {serviceType !== "pickup" && (
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1 block">
                            <MapPin className="w-3 h-3" /> Adresa miesta konania *
                        </label>
                        <input value={form.delivery_address} onChange={e => set("delivery_address", e.target.value)}
                            placeholder="Ulica, mesto, PSČ"
                            className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                    </div>
                )}

                {/* Contact */}
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Kontaktné údaje</label>
                    <div className="space-y-2.5">
                        <input value={form.name} onChange={e => set("name", e.target.value)}
                            placeholder="Meno a priezvisko *"
                            className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                        <div className="grid grid-cols-2 gap-2.5">
                            <input value={form.email} onChange={e => set("email", e.target.value)}
                                placeholder="Email *" type="email"
                                className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                            <input value={form.phone} onChange={e => set("phone", e.target.value)}
                                placeholder="Telefón"
                                className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                        </div>
                        <textarea value={form.message} onChange={e => set("message", e.target.value)}
                            rows={2} placeholder="Poznámka (voliteľné)"
                            className="w-full px-3 py-2.5 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 resize-none" />
                    </div>
                </div>

                {/* Recap */}
                <div className="rounded-xl border border-nova-cyan/20 bg-nova-cyan/4 overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-nova-cyan/15 flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-nova-cyan" />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-nova-cyan">Rekapitulácia objednávky</span>
                    </div>
                    <div className="px-4 py-3 space-y-3">
                        {/* Items */}
                        <div className="space-y-1.5">
                            {items.map(({ equipment: eq, qty }) => (
                                <div key={eq.id} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-1.5 h-1.5 rounded-full bg-nova-cyan/50 shrink-0" />
                                        <span className="text-zinc-300 text-xs truncate">{eq.brand} {eq.model} ×{qty}</span>
                                    </div>
                                    {eq.rental_price_day > 0 ? (
                                        <span className="text-zinc-400 text-xs font-mono shrink-0">{(eq.rental_price_day * qty * days).toFixed(0)} €</span>
                                    ) : (
                                        <span className="text-zinc-600 text-[10px] shrink-0">na dopyt</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-white/6 pt-2.5 space-y-1.5">
                            {/* Dates */}
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Termín</span>
                                <span className="text-zinc-300 font-medium">{formatDate(startDate)} – {formatDate(endDate)}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-zinc-500 pl-4">Počet dní</span>
                                <span className="text-zinc-300">{days} {days === 1 ? "deň" : days < 5 ? "dni" : "dní"}</span>
                            </div>
                            {/* Service */}
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-zinc-500 flex items-center gap-1.5">
                                    {serviceType === "pickup" ? <Package className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                                    Odovzdanie
                                </span>
                                <span className="text-zinc-300">{SERVICE_OPTIONS.find(o => o.id === serviceType)?.label}</span>
                            </div>
                        </div>

                        {/* Total */}
                        {items.every(i => i.equipment.rental_price_day > 0) && (
                            <div className="border-t border-nova-cyan/20 pt-2.5 flex items-center justify-between">
                                <span className="text-zinc-400 text-xs">Orientačná cena spolu</span>
                                <span className="text-nova-cyan font-heading font-black text-base">
                                    {items.reduce((s, i) => s + i.equipment.rental_price_day * i.qty * days, 0).toFixed(0)} €
                                </span>
                            </div>
                        )}
                        <p className="text-zinc-600 text-[10px] leading-relaxed">
                            Záväzná cena bude potvrdená po overení dostupnosti. Doprava a inštalácia sú kalkulované samostatne.
                        </p>
                    </div>
                </div>
            </div>
            <div className="px-6 py-4 border-t border-white/8 flex gap-3">
                <button onClick={onBack} className="h-11 px-5 rounded-xl border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">
                    Späť
                </button>
                <button onClick={submit} disabled={sending}
                    className="flex-1 h-11 rounded-xl bg-nova-cyan text-black font-bold text-sm hover:saturate-125 disabled:opacity-50 transition-all">
                    {sending ? "Odosielam…" : "Odoslať žiadosť o rezerváciu"}
                </button>
            </div>
        </div>
    );
}

// ── Step 4: Confirmation ──────────────────────────────────────────────────────
function StepDone({ onClose }) {
    return (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 15 }}>
                <div className="w-16 h-16 rounded-full bg-nova-cyan/15 border border-nova-cyan/30 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-8 h-8 text-nova-cyan" />
                </div>
            </motion.div>
            <h3 className="font-heading font-black text-2xl tracking-tight mb-2">Žiadosť odoslaná!</h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-8 max-w-xs">
                Vaša žiadosť o rezerváciu bola prijatá. Ozveme sa vám do 24 hodín s potvrdením termínu.
            </p>
            <button onClick={onClose}
                className="h-11 px-8 rounded-xl bg-nova-cyan text-black font-bold text-sm hover:saturate-125 transition-all">
                Zatvoriť
            </button>
        </div>
    );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function BookingModal({ onClose }) {
    const { items, updateQty, removeItem, clearCart } = useCart();
    const [step, setStep]       = useState(1);
    const [startDate, setStart] = useState(null);
    const [endDate,   setEnd]   = useState(null);

    const handleDateChange = ({ startDate: s, endDate: e }) => { setStart(s); setEnd(e); };

    const handleDone = () => {
        clearCart();
        setStep(4);
    };

    if (items.length === 0 && step === 1) {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-start justify-end">
                <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
                <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                    transition={{ type: "spring", damping: 30, stiffness: 300 }}
                    className="relative w-full max-w-md h-screen bg-[#0d0f14] border-l border-white/8 flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                        <span className="font-heading font-black text-white">Košík</span>
                        <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                        <ShoppingCart className="w-10 h-10 text-zinc-700 mb-3" />
                        <p className="text-zinc-500 text-sm">Košík je prázdny. Pridajte techniku zo stránky prenájmu.</p>
                    </div>
                </motion.div>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-end">
            <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="relative w-full max-w-lg h-screen bg-[#0d0f14] border-l border-white/8 flex flex-col shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 shrink-0">
                    <div className="flex items-center gap-2.5">
                        <ShoppingCart className="w-4 h-4 text-nova-cyan" />
                        <span className="font-heading font-black text-white">Rezervácia</span>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {step < 4 && <Steps current={step} />}

                <div className="flex-1 flex flex-col min-h-0">
                    <AnimatePresence mode="wait">
                        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.18 }} className="flex flex-col h-full">
                            {step === 1 && <StepCart items={items} updateQty={updateQty} removeItem={removeItem} onNext={() => setStep(2)} />}
                            {step === 2 && <StepDates items={items} startDate={startDate} endDate={endDate} onChange={handleDateChange} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
                            {step === 3 && <StepContact items={items} startDate={startDate} endDate={endDate} onBack={() => setStep(2)} onDone={handleDone} />}
                            {step === 4 && <StepDone onClose={onClose} />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
}
